// Who actually gets to the water, and who actually gets to the feed
// (PLAN-04 S4, REFOCUS §7). The old model had one waterer and one feeder and
// everybody used them. Real flocks do not: birds queue, the ones at the bottom
// of the pecking order are kept off a single station they can all see, and a
// bird that never reaches the trough does not eat less — she goes without.
//
// The hourly roll is
//
//     p = base x familiarity x rankFactor x acceptance x personality
//
// and a bird who fails it drinks and eats for the share of the hour she got,
// and loses ground for the rest of it (see physiology.ts). That last part is
// what makes a second, out-of-sight feeder worth an eggit.
//
// Deterministic like everything else in src/sim: the roll is drawn from a named
// stream keyed to the bird and the sim-hour, so live play, offline catch-up and
// a future server replay all reach the same answer.

import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import type {
  BirdPersonality,
  ChickenEntity,
  GameState,
  HiddenStatId,
  Structure,
} from '../core/types';
import { getConfig, type BiologyConfig } from './config';
import { effectsOf } from './items';
import { rollStream } from './league';
import { waterTempF } from './microclimate';
import { isStructureRelearning, spotForStructure } from './spots';

/** Which kind of need a station answers. */
export type StationKind = 'water' | 'feed';

/** The intake classes the feed tables are published for. */
export type FeedClass = 'light' | 'heavy' | 'bantam';

/** Per-item gear space, in cm for a trough and in for a drinker. */
type GearSpace = { feederSpaceCm?: number; watererSpaceIn?: number };

/**
 * The two map tables are keyed by item id and by breed, so they are read
 * through a widened type rather than by widening the whole config: the JSON
 * gives exact keys, and only these two lookups are open-ended.
 */
function gearTable(config: BiologyConfig): Record<string, GearSpace> {
  return config.access.gear as Record<string, GearSpace>;
}

function feedTable(config: BiologyConfig): Record<FeedClass, Record<NeedStage, number>> {
  return config.intake.feedGPerDay as unknown as Record<FeedClass, Record<NeedStage, number>>;
}

// --- personality and hidden stats -------------------------------------------

/** Every hidden stat, so a loop cannot forget one. */
export const HIDDEN_STATS: HiddenStatId[] = ['eggs', 'hardiness', 'temperament'];

/**
 * Draws a bird's bearing and her three hidden numbers.
 *
 * Nothing is published for individual birds — no source gives a spread of eggs
 * or hardiness inside a breed, only the breed mean — so both the ±10% band and
 * the even split between bold and shy are design values. The reveal delay is
 * too (PLAN-04 S4 draft: 7 days of watching a bird before you can say anything
 * about her).
 *
 * Drawn from the caller's stream at the point of creation, after every other
 * draw `createChick` makes, so adding a bird's bearing does not renumber the
 * ids and genotypes that came before it.
 */
export function assignPersonality(
  rng: Rng,
  joinedOnDay: number,
  config: BiologyConfig = getConfig('normal'),
): BirdPersonality {
  const p = config.personality;
  const spread = p.statVariationFraction;
  const stats = {} as Record<HiddenStatId, number>;
  const revealedAtDay = {} as Record<HiddenStatId, number>;
  for (const stat of HIDDEN_STATS) {
    // Rounded to 1dp so a save, a replay and a UI that shows the number all
    // read the same value; a 0.9000000000000001 saved as JSON comes back a
    // different double and the replay stops being bit-for-bit.
    stats[stat] = round3(1 + rng.range(-spread, spread));
    revealedAtDay[stat] = joinedOnDay + p.revealDays;
  }
  return {
    temperament: rng.chance(p.boldShare) ? 'bold' : 'shy',
    stats,
    revealedAtDay,
  };
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/**
 * The real multiplier for a hidden stat. The simulation always uses the true
 * value; only the player is kept in the dark, and only until the reveal day.
 */
export function hiddenStatMultiplier(chicken: ChickenEntity, stat: HiddenStatId): number {
  return chicken.personality?.stats[stat] ?? 1;
}

/** True once the keeper has watched the bird long enough to read a stat. */
export function isStatRevealed(chicken: ChickenEntity, stat: HiddenStatId, day: number): boolean {
  const reveal = chicken.personality?.revealedAtDay[stat];
  return reveal === undefined || day >= reveal;
}

/**
 * How many more days of watching this stat needs, 0 once it is out. This is the
 * number the bird screen counts down, and it is this bird's own draw rather
 * than a curve off her age: a bird bought as a pullet has already been watched
 * longer than a day-old chick, which an age-based reveal cannot tell apart.
 */
export function statRevealDaysLeft(
  chicken: ChickenEntity,
  stat: HiddenStatId,
  day: number,
): number {
  const reveal = chicken.personality?.revealedAtDay[stat];
  if (reveal === undefined) return 0;
  return Math.max(0, reveal - day);
}

/** The stats a keeper can read today, with the value each one shows. */
export function revealedStats(
  chicken: ChickenEntity,
  day: number,
): Array<{ stat: HiddenStatId; value: number; revealed: boolean }> {
  return HIDDEN_STATS.map((stat) => ({
    stat,
    value: hiddenStatMultiplier(chicken, stat),
    revealed: isStatRevealed(chicken, stat, day),
  }));
}

// --- station layout pressure ------------------------------------------------

/** The gear space one item of a kind provides, in its own unit. */
export function gearSpace(itemId: string, kind: StationKind, config: BiologyConfig): number {
  const gear = gearTable(config)[itemId];
  if (gear) {
    const value = kind === 'feed' ? gear.feederSpaceCm : gear.watererSpaceIn;
    if (typeof value === 'number') return value;
  }
  return kind === 'feed'
    ? config.access.defaultFeederSpaceCm
    : config.access.defaultWatererSpaceIn;
}

/** Birds in the run: alive, and not out past the fence. */
function birdsInRun(state: GameState): ChickenEntity[] {
  return state.flock.filter((c) => c.alive && c.currentState !== 'ESCAPED');
}

/**
 * The trough space one bird needs before the flock is comfortably fed.
 *
 * BHV-16 carries two figures and they are not interchangeable: the EU's 10 cm and
 * the 12 cm guideline are the layer welfare end, while the FAO's "at least 4 cm
 * per bird for circular feeders" is the chick one. A chick is a fraction of a
 * hen's width and a chick pan is a fraction of a hen's rim, so charging a chick
 * flock the layer figure would have called every starter yard under-provisioned
 * before the player had done anything. A flock with any adult in it is held to
 * the layer figure, because the layer is the bird that has to get to the trough.
 * Both this and the per-bird share below read the run, not the roster: a hen out
 * past the fence is not taking up rim she cannot reach.
 */
export function feederSpaceFloor(
  state: GameState,
  config: BiologyConfig = getConfig('normal'),
): number {
  const hasAdult = birdsInRun(state).some(
    (c) => c.stage === 'adult' || c.stage === 'pullet',
  );
  return hasAdult ? config.access.feederSpaceCmWelfareMin : config.access.chickFeederSpaceCmMin;
}

/**
 * What the whole flock's station layout does to the bottom of the order.
 *
 * BHV-17 gives the mechanism and nothing else: insufficient feeder space makes
 * a trough a place where low-ranking hens are *excluded*, not merely slow, and
 * industry practice assumes equal shares at the ration — which is exactly the
 * assumption that fails. How many birds and how hard is unpublished (GIMME-DATA
 * C1, C6), so the fraction and the chance are design values; the space itself
 * is sourced.
 *
 * Water is deliberately outside this. BHV-17's row is about feeding, and
 * BHV-18 gives water space without any exclusion claim attached; a bird that is
 * thirsty pushes through a dominant one for a drink. So a drinker reports its
 * crowding and never excludes anybody — its cost to a low-ranking bird is the
 * temperature of the water and how busy the spot is.
 */
export interface StationPressure {
  kind: StationKind;
  /** Stations the flock can actually use right now. */
  stations: number;
  /** Of those, how many sit where the boss cannot watch them. */
  outOfView: number;
  /** Space per bird across the usable stations, in cm (feed) or in (water). */
  spacePerBird: number;
  /** Below the welfare figure for the species. */
  crowded: boolean;
  /** One station, and the boss has a sightline to it. */
  singleVisible: boolean;
  /** Chance the bottom of the order is kept off this hour. */
  excludedChance: number;
}

/** A station the flock can use: water that is there, or feed that is there. */
export function usableStations(state: GameState, kind: StationKind): Structure[] {
  return state.structures.filter((structure) => {
    const effects = effectsOf(structure);
    if (kind === 'water') {
      return Boolean(effects.providesWater) && (structure.state.water ?? 0) > 5;
    }
    return Boolean(effects.providesFeed) && (structure.state.feed ?? 0) > 5;
  });
}

/** The layout pressure the flock's stations put on the bottom of the order. */
export function stationPressure(
  state: GameState,
  kind: StationKind,
  config: BiologyConfig = getConfig('normal'),
): StationPressure {
  const a = config.access;
  const usable = usableStations(state, kind);
  const birds = Math.max(1, birdsInRun(state).length);
  const spacePerBird = usable.reduce((sum, s) => sum + gearSpace(s.itemId, kind, config), 0) / birds;
  const outOfView = usable.filter((s) => {
    const spot = spotForStructure(state, s);
    // A station with no spot is not in the boss's sightline map at all, so it
    // counts as out of view: that is where gear bought and never placed was
    // always effectively standing.
    return !spot || !spot.bossVisible;
  }).length;
  const minimum = kind === 'feed' ? feederSpaceFloor(state, config) : a.watererSpaceInPerBird;
  const crowded = usable.length > 0 && spacePerBird < minimum;
  const singleVisible = usable.length === 1 && outOfView === 0;
  const relieved = usable.length >= 2 && outOfView > 0 && !crowded;
  // Only feed carries the exclusion; see StationPressure. Both branches of the
  // plan's rule are here — not enough space, or one station the boss can see —
  // but they are not equally bad. BHV-16's stated design criterion is that ALL
  // the birds can feed at once, so with the space for that there is no queue to
  // be excluded from, and the watched trough costs a low-ranking bird a share
  // of her hours rather than most of them.
  const excludedChance =
    kind !== 'feed' || (!crowded && !singleVisible)
      ? 0
      : crowded
        ? a.crowdedExclusionChance
        : relieved
          ? a.reliefExclusionChance
          : a.adequateSpaceExclusionChance;
  return { kind, stations: usable.length, outOfView, spacePerBird, crowded, singleVisible, excludedChance };
}

/**
 * The flock's trough shortfall, in the one row the player is taught from.
 *
 * Both numbers are the sim's own: `spacePerBird` is the figure the hourly
 * access roll divides by and `needPerBird` is the floor that same roll compares
 * it against, so a screen that prints them cannot quote a figure the simulation
 * did not produce. Null when the flock is not short — which includes a feeder
 * that is standing but empty, because there is nothing to crowd around yet.
 */
export interface FeederShortfall {
  /** Trough space per bird the flock has right now, in cm. */
  spacePerBird: number;
  /** The figure `feederSpaceFloor` holds a flock with a layer in it to. */
  needPerBird: number;
  /** Live birds in the run, which is what the space is divided between. */
  birds: number;
  /** Feeders the flock can use, and therefore the ones carrying the space. */
  stations: number;
  /** Cm each bird is short by. 0 is not possible here: this row is the short case. */
  shortBy: number;
}

/**
 * Why the flock is short of trough, or null when it is not.
 *
 * The starter pan is 40 cm and a layer flock is held to BHV-16's 10 cm each, so
 * the free kit is four birds' worth and the crossing falls at five. That is a
 * property of the flock and the pan rather than a constant, and it is read here
 * instead of written down anywhere: a UI that did its own division could round
 * it differently from the roll and teach a number the sim never used.
 */
export function feederShortfall(
  state: GameState,
  config: BiologyConfig = getConfig('normal'),
): FeederShortfall | null {
  const pressure = stationPressure(state, 'feed', config);
  if (!pressure.crowded) return null;
  const needPerBird = feederSpaceFloor(state, config);
  return {
    spacePerBird: pressure.spacePerBird,
    needPerBird,
    birds: Math.max(1, birdsInRun(state).length),
    stations: pressure.stations,
    shortBy: needPerBird - pressure.spacePerBird,
  };
}

/** How many birds at the bottom of the order the pressure reaches. */
export function excludedTailSize(
  flockSize: number,
  excludedChance: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  if (excludedChance <= 0 || flockSize <= 1) return 0;
  return Math.max(1, Math.round(flockSize * config.access.excludedRankFraction));
}

/** True when this bird is in the part of the order the pressure reaches. */
export function isExcluded(
  chicken: ChickenEntity,
  pressure: StationPressure,
  flockSize: number,
  config: BiologyConfig = getConfig('normal'),
): boolean {
  const tail = excludedTailSize(flockSize, pressure.excludedChance, config);
  if (tail === 0) return false;
  return chicken.hierarchyRank >= flockSize - tail;
}

// --- the hourly roll ---------------------------------------------------------

/**
 * How well this bird can use this station this hour.
 *
 * - `familiarity` halves while a moved item is still being relearned: the birds
 *   know the trough is not where it was (REFOCUS §7; BHV-19 is UNRESOLVED, so
 *   the size of the penalty is a design value).
 * - `rankFactor` is the exclusion above, and it is 1 for everyone else.
 * - `acceptance` is the water-temperature penalty (GIMME-DATA B1: intake is
 *   normal at or below 85 degF, "less" through the 85-95 band and "much less"
 *   above it). Only drinkable water has a temperature, so a feeder is 1.
 * - `personality` is the shy bird's reluctance to push into a busy spot.
 */
export function accessProbability(
  chicken: ChickenEntity,
  state: GameState,
  structure: Structure,
  kind: StationKind,
  pressure: StationPressure,
  config: BiologyConfig = getConfig('normal'),
): number {
  return probabilityFor(
    chicken,
    state.flock.length,
    describeStation(state, structure, kind, config),
    pressure,
    config,
  );
}

/** The parts of a station's odds that are the same for every bird. */
export interface StationOdds {
  structure: Structure;
  /** Still being relearned after a move. */
  relearning: boolean;
  /** The water-temperature penalty; always 1 for a feeder. */
  acceptance: number;
  /** How busy the spot is. */
  traffic: number;
}

/**
 * Reads a station's own odds once.
 *
 * The spot lookup walks the whole spot table, and the hourly roll asks about
 * every station on behalf of every bird. Left per-bird it made a 180-day
 * playtest miss its budget, so the per-station half is hoisted out here and the
 * per-bird loop only does what actually differs between birds.
 */
export function describeStation(
  state: GameState,
  structure: Structure,
  kind: StationKind,
  config: BiologyConfig = getConfig('normal'),
): StationOdds {
  const spot = spotForStructure(state, structure);
  return {
    structure,
    relearning: isStructureRelearning(state, structure),
    acceptance: kind === 'water' ? waterAcceptance(structure, config) : 1,
    traffic: spot?.traffic ?? config.access.defaultTraffic,
  };
}

function probabilityFor(
  chicken: ChickenEntity,
  flockSize: number,
  station: StationOdds,
  pressure: StationPressure,
  config: BiologyConfig,
): number {
  const a = config.access;
  let p = a.base;
  if (station.relearning) p *= a.familiarityWhileRelearning;
  if (isExcluded(chicken, pressure, flockSize, config)) p *= 1 - pressure.excludedChance;
  p *= station.acceptance;
  p *= personalityFactor(chicken, station.traffic, config);
  return clamp(p, 0, 1);
}

/**
 * How much of her need a bird actually takes from this drinker, by the
 * temperature of the water in it. GIMME-DATA B1 fixes the bands and calls the
 * effect size UNRESOLVED, so the shape follows the bands and the slope is a
 * design value.
 */
export function waterAcceptance(
  structure: Structure,
  config: BiologyConfig = getConfig('normal'),
): number {
  const a = config.access;
  const temp = waterTempF(structure);
  // No modelled temperature yet (a drinker filled seconds ago) is not evidence
  // of cold water, so it carries no penalty.
  if (temp === undefined || temp <= a.waterAcceptanceFlatF) return 1;
  const drop = (temp - a.waterAcceptanceFlatF) * a.waterAcceptancePerDegF;
  return clamp(1 - drop, a.waterAcceptanceFloor, 1);
}

/** A shy bird will not push into a busy spot; a bold one will. */
export function personalityFactor(
  chicken: ChickenEntity,
  traffic: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  if (chicken.personality?.temperament !== 'shy') return 1;
  return clamp(1 - config.personality.shyTrafficPenalty * traffic, 0, 1);
}

/**
 * Rolls access for every living bird, once per sim-hour.
 *
 * The result is written onto the bird and keyed by the hour it was made for, so
 * the four quarter-hour steps of an offline catch-up share one roll and a
 * one-second live tick does not re-draw it thirty times.
 */
export function rollHourlyAccess(state: GameState, config: BiologyConfig = getConfig('normal')): void {
  const hour = Math.floor(state.simHour);
  const water = stationPressure(state, 'water', config);
  const feed = stationPressure(state, 'feed', config);
  const waterOdds = usableStations(state, 'water').map((s) => describeStation(state, s, 'water', config));
  const feedOdds = usableStations(state, 'feed').map((s) => describeStation(state, s, 'feed', config));
  const flockSize = state.flock.length;

  for (const chicken of state.flock) {
    if (!chicken.alive) continue;
    if (chicken.access?.hour === hour) continue;
    // Keyed to the bird and the hour, not to call order, so a flock that gains
    // or loses a bird mid-hour does not renumber everybody else's rolls.
    const rng = rollStream(state, `access:${chicken.id}:${hour}`);
    chicken.access = {
      hour,
      water: reachAny(chicken, flockSize, waterOdds, water, config, rng),
      feed: reachAny(chicken, flockSize, feedOdds, feed, config, rng),
    };
  }
}

/** 1 when the bird reached at least one station of this kind this hour. */
function reachAny(
  chicken: ChickenEntity,
  flockSize: number,
  stations: StationOdds[],
  pressure: StationPressure,
  config: BiologyConfig,
  rng: Rng,
): number {
  for (const station of stations) {
    if (rng.chance(probabilityFor(chicken, flockSize, station, pressure, config))) return 1;
  }
  return 0;
}

/**
 * The share of the last hour a bird reached water or feed, 0-1. A bird with no
 * roll at all — a save from before this model — reads as having reached both,
 * so upgrading a save cannot make a healthy flock look neglected.
 */
export function accessShare(chicken: ChickenEntity, kind: StationKind): number {
  return chicken.access ? (kind === 'water' ? chicken.access.water : chicken.access.feed) : 1;
}

// --- intake need -------------------------------------------------------------

type NeedStage = 'chick' | 'pullet' | 'adult';

function needStage(stage: ChickenEntity['stage']): NeedStage {
  if (stage === 'pullet') return 'pullet';
  return stage === 'adult' ? 'adult' : 'chick';
}

/**
 * How much more water this bird wants than a comfortable adult layer, from
 * NUT-10 (intake by stage) and NUT-11 (intake climbs with heat). Used to size
 * what a successful drink actually refills: a chick refilling from the same
 * drinker is not taking a layer's worth of water.
 */
export function waterNeedScale(
  chicken: ChickenEntity,
  tempF: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  const table = config.intake.waterMlPerDay;
  const stage = needStage(chicken.stage);
  return (table[stage] / table.adult) * waterNeedHeatScale(tempF, config);
}

/**
 * The heat part of the water need: 1 at or below 70 degF, the sourced 85 degF
 * and 95 degF multipliers at those temperatures, straight between. NUT-11 states
 * both anchors, so both anchors are what the model uses.
 */
export function waterNeedHeatScale(
  tempF: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  const n = config.intake.waterNeed;
  if (tempF <= n.referenceF) return 1;
  if (tempF >= 95) return n.at95F;
  if (tempF <= 85) return 1 + ((tempF - n.referenceF) / (85 - n.referenceF)) * (n.at85F - 1);
  return n.at85F + ((tempF - 85) / (95 - 85)) * (n.at95F - n.at85F);
}

/**
 * How much more food this bird wants than a heavy-breed layer, from the daily
 * intake tables: BRD-16 for the light breeds, BRD-17 for the heavies, BRD-18 for
 * the Silkie. A bantam eating a layer's ration is being overfed even when the
 * feeder is full.
 */
export function feedNeedScale(
  chicken: ChickenEntity,
  config: BiologyConfig = getConfig('normal'),
): number {
  const table = feedTable(config);
  const feedClass = feedClassOf(chicken.breed, config);
  return table[feedClass][needStage(chicken.stage)] / table[defaultFeedClass(config)].adult;
}

/** The class every other intake figure is normalised against. */
export function defaultFeedClass(config: BiologyConfig = getConfig('normal')): FeedClass {
  return config.intake.defaultFeedClass as FeedClass;
}

/** Which intake table a breed is read from, for the UI and the fact cards. */
export function feedClassOf(
  breed: ChickenEntity['breed'],
  config: BiologyConfig = getConfig('normal'),
): FeedClass {
  const classes = config.intake.feedClassByBreed as Record<string, FeedClass | undefined>;
  return classes[breed] ?? defaultFeedClass(config);
}
